/**
 * Simulation Types
 * Shared types for frontend simulation state
 */

/** Food type - determines visual and narrative origin */
export enum FoodType {
  PLANT = 'plant', // Green - spawns naturally
  MEAT = 'meat', // Red - from dead creatures
  MINERAL = 'mineral', // Blue - spawns at edges/special locations
}

export interface CreatureData {
  id: string;
  x: number;
  y: number;
  angle: number;
  size: number;
  color: { h: number; s: number; l: number };
  energy: number;
  maxEnergy: number;
}

export interface FoodData {
  id: string;
  x: number;
  y: number;
  energy: number;
  radius: number;
  type: FoodType;
  color?: number; // Hue for meat (inherited from creature)
}

export interface WorldStats {
  population: number;
  food: number;
  generation: number;
  species: number;
}

export interface WorldState {
  tick: number;
  creatures: CreatureData[];
  food: FoodData[];
  stats: WorldStats;
}

export interface SimulationStatus {
  state: 'stopped' | 'running' | 'paused';
  speed: number;
  tick: number;
  actualTPS: number;
  targetTPS: number;
  creatures: number;
  food: number;
}

export interface CreatureDetails {
  id: string;
  generation: number;
  parentId?: string | null;
  age: number;
  energy: number;
  maxEnergy: number;
  isAlive: boolean;
  isMature: boolean;
  position: { x: number; y: number };
  velocity: { x: number; y: number };
  angle: number;
  size: number;
  speciesId: number;
  fitness: number;
  // Owner info (for multiplayer)
  ownerId?: string | null;
  ownerName?: string | null;
}

export interface BrainData {
  numInputs: number;
  numOutputs: number;
  nodeCount: number;
  connectionCount: number;
  nodes: Array<{
    id: number;
    type: string;
    activation: string;
    layer: number;
  }>;
  connections: Array<{
    innovation: number;
    fromNode: number;
    toNode: number;
    weight: number;
    enabled: boolean;
  }>;
}

// ========== Multiplayer Types ==========

export interface GuidanceSettings {
  thrustBias: number;      // -0.5 to 0.5 (slow vs fast)
  aggression: number;      // -0.5 to 0.5 (flee vs attack)
  exploration: number;     // -0.5 to 0.5 (stay vs move)
  reproduction: number;    // -0.5 to 0.5 (avoid vs seek)
}

export interface UserStats {
  totalCreaturesSpawned: number;
  longestSurvival: number;
  mostOffspring: number;
}

export interface UserSession {
  sessionId: string;
  displayName: string;
  image: string | null;
  creatureId: string | null;
  isAdmin: boolean;
  stats: UserStats;
  guidance: GuidanceSettings;
}

export interface PlayerInfo {
  sessionId: string;
  displayName: string;
  image: string | null;
  creatureId: string | null;
  isConnected: boolean;
}

export interface LeaderboardEntry {
  sessionId: string;
  displayName: string;
  creatureId: string;
  value: number;
}

export interface Leaderboard {
  oldest: LeaderboardEntry[];
  mostOffspring: LeaderboardEntry[];
  highestEnergy: LeaderboardEntry[];
}

export interface PlayersUpdate {
  players: PlayerInfo[];
  leaderboard: Leaderboard;
  connectedCount: number;
}

export interface MyCreatureState {
  id: string | null;
  alive: boolean;
  energy?: number;
  age?: number;
  // Full creature data for immediate display (before world:state arrives)
  creature?: CreatureDataWithOwner;
}

// Extended creature data with owner info
export interface CreatureDataWithOwner extends CreatureData {
  ownerId: string | null;
  ownerName: string | null;
  isPlayerControlled: boolean;
}
